import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Eye, BookOpen, Camera, Home, Trophy, Brain, Flower, Library, ChevronDown, Moon, Sparkles, FlaskConical, Gavel, Wind, Coffee, Hourglass, Sunset, Feather, Flame, Droplets, Mountain, Scissors, Compass, Sun, Heart, UserCheck, TrendingUp, Footprints, Layers, Landmark, Scale, Sunrise, PauseCircle, Users } from 'lucide-react';
import { useUser } from '../context/UserContext';

const NavBar: React.FC = () => {
  const { user } = useUser();
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsLibraryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleBookSelect = (path: string) => {
    navigate(path);
    setIsLibraryOpen(false);
  };

  const getLinkClass = (isActive: boolean) => 
    `flex items-center gap-2 px-3 py-2 rounded-full transition-all duration-300 ${
      isActive 
        ? 'bg-nunchi-700 text-white shadow-md' 
        : 'text-nunchi-700 hover:bg-nunchi-100'
    }`;

  // Determine current book context
  let currentBookKey: 'nunchi' | 'ikigai' | 'zen' | 'ichigo' | 'letting_go' | 'courage' | 'happy' | 'kaizen' | 'stoic' | 'slow' = 'nunchi';
  let currentBookLabel = "Nunchi Master";

  if (location.pathname.startsWith('/ikigai')) {
    currentBookKey = 'ikigai';
    currentBookLabel = "Ikigai";
  } else if (location.pathname.startsWith('/zen-monkey')) {
    currentBookKey = 'zen';
    currentBookLabel = "Zen Monkey";
  } else if (location.pathname.startsWith('/ichigo-ichie')) {
    currentBookKey = 'ichigo';
    currentBookLabel = "Ichigo Ichie";
  } else if (location.pathname.startsWith('/letting-go')) {
    currentBookKey = 'letting_go';
    currentBookLabel = "Letting Go";
  } else if (location.pathname.startsWith('/courage')) {
    currentBookKey = 'courage';
    currentBookLabel = "Courage (Disliked)";
  } else if (location.pathname.startsWith('/happy')) {
    currentBookKey = 'happy';
    currentBookLabel = "Courage (Happy)";
  } else if (location.pathname.startsWith('/kaizen')) {
    currentBookKey = 'kaizen';
    currentBookLabel = "Kaizen";
  } else if (location.pathname.startsWith('/stoic')) {
    currentBookKey = 'stoic';
    currentBookLabel = "Stoicism";
  } else if (location.pathname.startsWith('/slow')) {
    currentBookKey = 'slow';
    currentBookLabel = "Slow Down";
  }

  // Define specific buttons for each book
  const navLinks = {
    nunchi: [
        { to: "/", icon: Home, label: "Home", exact: true },
        { to: "/learn", icon: BookOpen, label: "Rules", exact: false },
        { to: "/scenario", icon: Eye, label: "Dojo", exact: false },
        { to: "/assessment", icon: Brain, label: "Quiz", exact: false },
        { to: "/vibe-check", icon: Camera, label: "Vibe", exact: false },
        { to: "/leaderboard", icon: Trophy, label: "Rank", exact: false },
    ],
    ikigai: [
        { to: "/ikigai", icon: Flower, label: "Map", exact: true },
        { to: "/ikigai/lab", icon: FlaskConical, label: "Synthesis", exact: true },
        { to: "/ikigai/spark", icon: Sparkles, label: "Daily Spark", exact: true },
        { to: "/leaderboard", icon: Trophy, label: "Rank", exact: false },
    ],
    zen: [
        { to: "/zen-monkey", icon: Moon, label: "Library", exact: true },
        { to: "/zen-monkey/koan", icon: Gavel, label: "Koan Dojo", exact: true },
        { to: "/zen-monkey/garden", icon: Wind, label: "Garden", exact: true },
        { to: "/leaderboard", icon: Trophy, label: "Rank", exact: false },
    ],
    ichigo: [
        { to: "/ichigo-ichie", icon: Hourglass, label: "Principles", exact: true },
        { to: "/ichigo-ichie/tea-room", icon: Coffee, label: "Tea Room", exact: true },
        { to: "/ichigo-ichie/moments", icon: Sunset, label: "Golden Hour", exact: true },
        { to: "/leaderboard", icon: Trophy, label: "Rank", exact: false },
    ],
    letting_go: [
        { to: "/letting-go", icon: Feather, label: "Principles", exact: true },
        { to: "/letting-go/ceremony", icon: Flame, label: "Release", exact: true },
        { to: "/letting-go/flow", icon: Droplets, label: "Flow State", exact: true },
        { to: "/leaderboard", icon: Trophy, label: "Rank", exact: false },
    ],
    courage: [
        { to: "/courage", icon: Mountain, label: "Principles", exact: true },
        { to: "/courage/separation", icon: Scissors, label: "Task Separator", exact: true },
        { to: "/courage/shift", icon: Compass, label: "Freedom Shift", exact: true },
        { to: "/leaderboard", icon: Trophy, label: "Rank", exact: false },
    ],
    happy: [
        { to: "/happy", icon: Sun, label: "Principles", exact: true },
        { to: "/happy/love", icon: Heart, label: "Task of Love", exact: true },
        { to: "/happy/reliance", icon: UserCheck, label: "Self Reliance", exact: true },
        { to: "/leaderboard", icon: Trophy, label: "Rank", exact: false },
    ],
    kaizen: [
        { to: "/kaizen", icon: TrendingUp, label: "Principles", exact: true },
        { to: "/kaizen/steps", icon: Footprints, label: "Small Steps", exact: true },
        { to: "/kaizen/hansei", icon: Layers, label: "Hansei", exact: true },
        { to: "/leaderboard", icon: Trophy, label: "Rank", exact: false },
    ],
    stoic: [
        { to: "/stoic", icon: Landmark, label: "Principles", exact: true },
        { to: "/stoic/control", icon: Scale, label: "Control", exact: true },
        { to: "/stoic/morning", icon: Sunrise, label: "Morning Prep", exact: true },
        { to: "/leaderboard", icon: Trophy, label: "Rank", exact: false },
    ],
    slow: [
        { to: "/slow", icon: PauseCircle, label: "Principles", exact: true },
        { to: "/slow/pause", icon: PauseCircle, label: "The Pause", exact: true },
        { to: "/slow/connect", icon: Users, label: "Harmony", exact: true },
        { to: "/leaderboard", icon: Trophy, label: "Rank", exact: false },
    ]
  };

  const currentLinks = navLinks[currentBookKey];

  return (
    <div className="sticky top-4 z-50 mb-8">
      {/* Main Nav */}
      <nav className="mx-auto max-w-4xl bg-white/90 backdrop-blur-md border border-nunchi-200 rounded-full shadow-lg p-2 flex justify-between items-center relative">
        
        {/* Book Selector (Top Left) */}
        <div className="relative mr-2 border-r border-nunchi-100 pr-2" ref={dropdownRef}>
            <button 
                onClick={() => setIsLibraryOpen(!isLibraryOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-full hover:bg-nunchi-50 text-nunchi-800 font-bold text-xs sm:text-sm transition-colors"
            >
                <Library size={18} className="text-nunchi-600" />
                <span className="hidden sm:block">{currentBookLabel}</span>
                <ChevronDown size={14} className={`text-nunchi-400 transition-transform ${isLibraryOpen ? 'rotate-180' : ''}`} />
            </button>

            {isLibraryOpen && (
                <div className="absolute top-full left-0 mt-3 w-64 bg-white rounded-xl shadow-xl border border-nunchi-100 overflow-hidden z-[60] animate-fadeIn">
                    <div className="p-2 bg-nunchi-50 text-xs font-bold text-nunchi-400 uppercase tracking-widest border-b border-nunchi-100">
                        Select Book
                    </div>
                    <button onClick={() => handleBookSelect('/')} className="w-full text-left p-3 hover:bg-nunchi-50 flex items-center gap-3 transition-colors">
                        <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center"><Eye size={16} /></div>
                        <div>
                            <div className="font-bold text-nunchi-800 text-sm">Nunchi Master</div>
                            <div className="text-[10px] text-nunchi-500">The Art of Reading the Room</div>
                        </div>
                    </button>
                    <button onClick={() => handleBookSelect('/ikigai')} className="w-full text-left p-3 hover:bg-nunchi-50 flex items-center gap-3 transition-colors">
                        <div className="w-8 h-8 rounded-lg bg-pink-100 text-pink-600 flex items-center justify-center"><Flower size={16} /></div>
                         <div>
                            <div className="font-bold text-nunchi-800 text-sm">Ikigai</div>
                            <div className="text-[10px] text-nunchi-500">A Reason for Being</div>
                        </div>
                    </button>
                    <button onClick={() => handleBookSelect('/zen-monkey')} className="w-full text-left p-3 hover:bg-nunchi-50 flex items-center gap-3 transition-colors">
                        <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center"><Moon size={16} /></div>
                         <div>
                            <div className="font-bold text-nunchi-800 text-sm">Zen Monkey</div>
                            <div className="text-[10px] text-nunchi-500">52 Stories for Stillness</div>
                        </div>
                    </button>
                    <button onClick={() => handleBookSelect('/ichigo-ichie')} className="w-full text-left p-3 hover:bg-nunchi-50 flex items-center gap-3 transition-colors">
                        <div className="w-8 h-8 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center"><Hourglass size={16} /></div>
                         <div>
                            <div className="font-bold text-nunchi-800 text-sm">Ichigo Ichie</div>
                            <div className="text-[10px] text-nunchi-500">Making the Most of Every Moment</div>
                        </div>
                    </button>
                    <button onClick={() => handleBookSelect('/letting-go')} className="w-full text-left p-3 hover:bg-nunchi-50 flex items-center gap-3 transition-colors">
                        <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center"><Feather size={16} /></div>
                         <div>
                            <div className="font-bold text-nunchi-800 text-sm">Letting Go</div>
                            <div className="text-[10px] text-nunchi-500">The Power of Detachment</div>
                        </div>
                    </button>
                    <button onClick={() => handleBookSelect('/courage')} className="w-full text-left p-3 hover:bg-nunchi-50 flex items-center gap-3 transition-colors">
                        <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center"><Mountain size={16} /></div>
                         <div>
                            <div className="font-bold text-nunchi-800 text-sm">Courage (Disliked)</div>
                            <div className="text-[10px] text-nunchi-500">The Courage to Be Disliked</div>
                        </div>
                    </button>
                    <button onClick={() => handleBookSelect('/happy')} className="w-full text-left p-3 hover:bg-nunchi-50 flex items-center gap-3 transition-colors">
                        <div className="w-8 h-8 rounded-lg bg-yellow-100 text-yellow-600 flex items-center justify-center"><Sun size={16} /></div>
                         <div>
                            <div className="font-bold text-nunchi-800 text-sm">Happiness (Courage II)</div>
                            <div className="text-[10px] text-nunchi-500">The Courage to Be Happy</div>
                        </div>
                    </button>
                    <button onClick={() => handleBookSelect('/kaizen')} className="w-full text-left p-3 hover:bg-nunchi-50 flex items-center gap-3 transition-colors">
                        <div className="w-8 h-8 rounded-lg bg-green-100 text-green-600 flex items-center justify-center"><TrendingUp size={16} /></div>
                         <div>
                            <div className="font-bold text-nunchi-800 text-sm">Kaizen</div>
                            <div className="text-[10px] text-nunchi-500">The Japanese Art of Improvement</div>
                        </div>
                    </button>
                    <button onClick={() => handleBookSelect('/stoic')} className="w-full text-left p-3 hover:bg-nunchi-50 flex items-center gap-3 transition-colors">
                        <div className="w-8 h-8 rounded-lg bg-stone-100 text-stone-600 flex items-center justify-center"><Landmark size={16} /></div>
                         <div>
                            <div className="font-bold text-nunchi-800 text-sm">Stoicism</div>
                            <div className="text-[10px] text-nunchi-500">The Little Book of Stoicism</div>
                        </div>
                    </button>
                    <button onClick={() => handleBookSelect('/slow')} className="w-full text-left p-3 hover:bg-nunchi-50 flex items-center gap-3 transition-colors">
                        <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center"><PauseCircle size={16} /></div>
                         <div>
                            <div className="font-bold text-nunchi-800 text-sm">Slow Down</div>
                            <div className="text-[10px] text-nunchi-500">Things You Can See Only When...</div>
                        </div>
                    </button>
                </div>
            )}
        </div>

        {/* Dynamic Nav Buttons */}
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar flex-1">
            {currentLinks.map((link) => {
                const isActive = link.exact 
                  ? location.pathname === link.to 
                  : location.pathname.startsWith(link.to);
                
                return (
                    <Link 
                        key={link.to} 
                        to={link.to} 
                        className={getLinkClass(isActive)}
                    >
                        <link.icon size={18} />
                        <span className="hidden lg:inline text-xs font-medium">{link.label}</span>
                    </Link>
                );
            })}
        </div>

        {/* User Stats Badge */}
        <Link 
          to="/profile"
          className="hidden md:flex items-center gap-2 px-3 border-l border-nunchi-100 ml-2 flex-shrink-0 hover:bg-nunchi-50 rounded-lg transition-colors cursor-pointer"
        >
            <div className="text-right">
                <div className="text-[10px] uppercase text-nunchi-400 font-bold tracking-wider">{user.name}</div>
                <div className="text-xs font-bold text-nunchi-800">{user.xp} XP</div>
            </div>
            <div className="w-8 h-8 rounded-full bg-nunchi-100 flex items-center justify-center text-nunchi-600 font-serif font-bold">
                {user.level}
            </div>
        </Link>
      </nav>
      
      {/* Mobile Stats Bar */}
      <Link to="/profile" className="md:hidden flex justify-center mt-2 gap-4 text-xs font-medium text-nunchi-600 bg-white/50 py-1 px-4 rounded-full mx-auto w-max backdrop-blur-sm shadow-sm active:scale-95 transition-transform">
        <span>Level {user.level}</span>
        <span className="text-nunchi-300">|</span>
        <span>{user.xp} XP</span>
      </Link>
    </div>
  );
};

export default NavBar;